import TextFormField from '@map/components/formControls/TextFormField';
import { useAllowedLanguages } from '@map/hooks/locales';
import { makeStyles } from '@map/theme';
import { ListItem, Typography } from '@mui/material';
import Autocomplete, { AutocompleteProps } from '@mui/material/Autocomplete';
import { useSelfNamedInterfaceLocales } from '@shared/hooks/displayNames';
import { useFormatMessage } from '@shared/hooks/intl';
import useToggleState from '@shared/hooks/useToggleState';
import ChevronDownIcon from '@ui/Icon/library/ChevronDownIcon';
import WebsiteIcon from '@ui/Icon/library/WebsiteIcon';
import LanguageAcronym from '@ui/LanguageAcronym';
import Select from '@ui/formElements/Select';
import { Fragment, useCallback } from 'react';

const useStyles = makeStyles(({ borderRadius, spacing, sizing, palette }) => ({
  acronym: {
    marginRight: spacing(2),
    flex: '0 0 auto',
  },
  acronymShort: {
    marginRight: spacing(1),
    flex: '0 0 auto',
  },
  shortArrow: {
    color: palette.primary[500],
    marginRight: spacing(1.5),
    marginLeft: spacing(1.5),
  },
  shortField: {
    borderRadius: borderRadius('m'),
    border: 'none',
  },
  shortInput: {
    '&&': {
      background: palette.grey[50],
    },
    cursor: 'pointer',
    width: sizing(22),
    border: 'none',
    padding: spacing(2),
  },
  shortFieldBackgroundless: {
    backgroundColor: 'transparent',
  },
  endAdornment: {
    marginRight: spacing(3),
  },
  websiteIcon: {
    color: palette.neutrals[700],
    marginRight: spacing(2),
  },
  adornmentStyle: {
    display: 'flex',
    alignItems: 'center',
  },
}));

type Props = {
  variant?: 'default' | 'short';
  value: string;
  onChange: (value: string) => void;
};

const LanguagePicker = ({ variant = 'default', value, onChange }: Props) => {
  const t = useFormatMessage();
  const { classes, cx } = useStyles();

  const isShort = variant === 'short';
  const allowedLanguages = useAllowedLanguages();
  const locales = useSelfNamedInterfaceLocales(allowedLanguages);
  const currentLocale = locales.find(({ code }) => code === value);

  const [isOpen, open, close] = useToggleState(false);
  const toggle = useCallback(() => (isOpen ? close() : open()), [close, isOpen, open]);

  type BoundAcProps = Required<AutocompleteProps<(typeof locales)[number], false, false, false>>;

  const startAdornment = currentLocale ? (
    <div className={classes.adornmentStyle}>
      <WebsiteIcon color="primary" className={classes.websiteIcon} fontSize="small" />
      <LanguageAcronym
        className={cx(isShort ? classes.acronymShort : classes.acronym)}
        localeId={currentLocale.code}
        aria-label={currentLocale.name}
      />
    </div>
  ) : null;

  const renderOption = useCallback<BoundAcProps['renderOption']>(
    (props, option) => (
      <ListItem {...props}>
        <Typography>{option.name}</Typography>
      </ListItem>
    ),
    [],
  );

  if (isShort) {
    return (
      <Select
        native={false}
        SelectProps={{
          startAdornment,
          open: isOpen,
          SelectDisplayProps: {
            style: { minWidth: 1, padding: 0, opacity: 0 },
          },
          onClose: close,
          onChange: (event) => {
            if (event.target.value && typeof event.target.value === 'string') {
              onChange(event.target.value);
              close();
            }
          },
          MenuProps: { BackdropComponent: Fragment, disableScrollLock: true },
          className: classes.shortInput,
        }}
        IconProps={{
          fontSize: 'small',
          className: classes.shortArrow,
        }}
        aria-autocomplete="none"
        aria-label={t('choose_locale.title')}
        value={value}
        onClick={toggle}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && !isOpen) {
            open();
          }
        }}
        className={cx(classes.shortField, classes.shortFieldBackgroundless)}
        options={locales.map((locale) => {
          return { ...locale, label: locale.name, key: locale.code, value: locale.code };
        })}
      />
    );
  }

  return (
    <Autocomplete
      aria-label={t('choose_locale.title')}
      open={isOpen}
      autoSelect
      selectOnFocus
      autoHighlight
      onOpen={open}
      onClose={close}
      multiple={false}
      options={locales}
      // disableClearable doesn't work as expected and values can still be null
      disableClearable={true as false}
      value={currentLocale}
      getOptionLabel={({ name }) => name}
      renderOption={renderOption}
      onChange={(_, option) => {
        if (option) {
          onChange(option.code);
        }
      }}
      classes={{ endAdornment: classes.endAdornment }}
      popupIcon={<ChevronDownIcon color="primary" />}
      renderInput={({ id, disabled, InputProps, InputLabelProps: _, inputProps }) => (
        <TextFormField
          id={id}
          name="language"
          disabled={disabled}
          InputProps={InputProps}
          inputProps={{ ...inputProps, autoComplete: 'off' }}
        />
      )}
    />
  );
};

export default LanguagePicker;
